import { useEffect, useRef, useState } from "react";
import { router } from "@inertiajs/react";
import { Check, ChevronDown } from "lucide-react";
import useDelayedLoading from "@/hooks/use-delayed-loading";
import { mergedQueryParams } from "@/utils/queryParams";
import { SelectOption } from "./Select";

interface TableFilterProps {
  label?: string;
  queryParam: string;
  value: string;
  options: SelectOption[];
  defaultValue?: string;
  only?: string[];
  onLoadingChange?: (loading: boolean) => void;
}

const TableFilter: React.FC<TableFilterProps> = ({
  label,
  queryParam,
  value,
  options,
  defaultValue,
  only,
  onLoadingChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const highlightedRef = useRef<HTMLLIElement>(null);
  const onLoadingChangeRef = useRef(onLoadingChange);
  const { loading, start, stop } = useDelayedLoading();

  const selectedIndex = options.findIndex((option) => option.value === value);
  const selectedLabel = options[selectedIndex]?.label ?? "";
  const isFiltered = defaultValue !== undefined && value !== defaultValue;

  useEffect(() => {
    onLoadingChangeRef.current = onLoadingChange;
  }, [onLoadingChange]);

  useEffect(() => {
    onLoadingChangeRef.current?.(loading);
  }, [loading]);

  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) highlightedRef.current?.scrollIntoView({ block: "nearest" });
  }, [isOpen, highlightedIndex]);

  const open = () => {
    setHighlightedIndex(selectedIndex === -1 ? 0 : selectedIndex);
    setIsOpen(true);
  };

  const handleSelect = (option: SelectOption) => {
    setIsOpen(false);
    buttonRef.current?.focus();

    if (option.value === value) return;

    router.get(
      window.location.pathname,
      mergedQueryParams(queryParam, option.value),
      {
        only,
        preserveState: true,
        preserveScroll: true,
        replace: true,
        onStart: start,
        onFinish: stop,
      },
    );
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!isOpen) {
      if (["ArrowDown", "ArrowUp", "Enter", " "].includes(event.key)) {
        event.preventDefault();
        open();
      }
      return;
    }

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setHighlightedIndex((index) => (index + 1) % options.length);
        break;
      case "ArrowUp":
        event.preventDefault();
        setHighlightedIndex(
          (index) => (index - 1 + options.length) % options.length,
        );
        break;
      case "Home":
        event.preventDefault();
        setHighlightedIndex(0);
        break;
      case "End":
        event.preventDefault();
        setHighlightedIndex(options.length - 1);
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        handleSelect(options[highlightedIndex]);
        break;
      case "Escape":
        event.preventDefault();
        setIsOpen(false);
        break;
      case "Tab":
        setIsOpen(false);
        break;
    }
  };

  return (
    <div className="flex w-full flex-col gap-2" ref={containerRef}>
      {label && (
        <label className="text-cream-800 dark:text-cream-400 text-sm font-medium">
          {label}
        </label>
      )}
      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          onClick={() => (isOpen ? setIsOpen(false) : open())}
          onKeyDown={handleKeyDown}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          className="text-cream-950 dark:bg-cream-800 dark:text-cream-100 focus:ring-peach-300 flex w-full cursor-pointer items-center justify-between gap-2 rounded-xl border border-transparent bg-white px-3 py-2 text-base font-medium shadow focus:ring-2 focus:outline-none"
        >
          <span className="flex min-w-0 items-center gap-2">
            {isFiltered && (
              <span
                aria-hidden
                className="bg-peach-500 h-2 w-2 shrink-0 rounded-full"
              />
            )}
            <span className="truncate">{selectedLabel}</span>
          </span>
          <ChevronDown
            size={16}
            strokeWidth={1.5}
            className={`text-cream-700 dark:text-cream-300 shrink-0 transition-transform ${
              isOpen ? "rotate-180" : ""
            }`.trim()}
          />
        </button>
        {isOpen && (
          <ul
            role="listbox"
            className="border-cream-300 dark:border-cream-700 dark:bg-cream-900 absolute top-full left-0 z-10 mt-2 max-h-60 w-full overflow-auto rounded-xl border bg-white shadow-lg"
          >
            {options.map((option, index) => (
              <li
                key={option.value}
                ref={index === highlightedIndex ? highlightedRef : null}
                role="option"
                aria-selected={option.value === value}
                onMouseDown={() => handleSelect(option)}
                onMouseEnter={() => setHighlightedIndex(index)}
                className={`flex cursor-pointer items-center justify-between gap-3 px-4 py-3 ${
                  index === highlightedIndex
                    ? "bg-cream-100 dark:bg-cream-800"
                    : "hover:bg-cream-50 dark:hover:bg-cream-800"
                } ${option.value === value ? "font-semibold" : ""}`.trim()}
              >
                {option.label}
                {option.value === value && (
                  <Check
                    size={16}
                    strokeWidth={2}
                    className="text-peach-500 shrink-0"
                  />
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default TableFilter;
